import { useContext, useId, useState } from 'react';
import { type Control, type UseFormSetValue, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Disclosure } from '@/components/molecules/Disclosure';
import type { PenRef, ProviderGroup, ProviderSchema } from '@/domain/providerConfig';
import { penKey, toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import { ClientAdminViewContext } from '@/routes/shared/ProviderConfig/ProviderSchemaField';

import type { FieldConflict } from '../form/conflictMerge';
import { formFields, type ProviderFormValues } from '../form/formModel';

import { SiteField } from './SiteField';
import { needsAttention } from './sectionFields';
import { UnknownExtraConfig } from './UnknownExtraConfig';

import layout from './fieldLayout.module.scss';
import styles from './sections.module.scss';

export interface ConnectionSectionProps {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    control: Control<ProviderFormValues>;
    /** A site value typed over pens that differ clears the pens' own values (R16a). */
    setValue: UseFormSetValue<ProviderFormValues>;
    conflicts: FieldConflict[];
}

/** The provider's site settings, as its schema lists them. */
function ConnectionSection({
    schema,
    group,
    pens,
    control,
    setValue,
    conflicts,
}: ConnectionSectionProps) {
    const { t } = useTranslation();
    // Every viewer reads the internal settings and the stored keys no schema names; only
    // OptoScale admins change them, so a client admin's boxes there are read-only.
    const clientAdmin = useContext(ClientAdminViewContext);
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const siteFields = formFields(schema, { includeAdvanced: true }).filter(
        (field) => field.scope === 'site'
    );
    const advancedFields = siteFields.filter((field) => field.advanced);
    const advancedPaths = advancedFields.flatMap((field): string[] => {
        const formKey = toFormKey(field.key);
        return [
            `connection.${formKey}`,
            ...pens.map((pen) => `pens.${penKey(pen.cageId)}.fields.${formKey}`),
        ];
    });
    const { errors } = useFormState({ control });
    // A save's error or a conflict on a hidden setting must not stay out of sight.
    const advancedShown = advancedOpen || needsAttention(errors, conflicts, advancedPaths);
    const headingId = `${id}-heading`;

    function renderField(field: (typeof siteFields)[number]) {
        return (
            <SiteField
                key={field.key}
                field={field}
                group={group}
                pens={pens}
                control={control}
                setValue={setValue}
                conflicts={conflicts}
            />
        );
    }

    return (
        <section className={styles.section} aria-labelledby={headingId}>
            <h3 id={headingId} className={styles.heading}>
                {t('providerConfig.sections.connection')}
            </h3>
            <div className={classNames(styles.fields, layout.narrow)}>
                {siteFields.filter((field) => !field.advanced).map(renderField)}
            </div>
            {advancedFields.length > 0 && (
                <Disclosure
                    title={t('providerConfig.sections.advanced')}
                    open={advancedShown}
                    onOpenChange={setAdvancedOpen}
                    radius="sm"
                    spacing="none"
                    panelClassName={styles.advancedPanel}>
                    {clientAdmin && (
                        <p className={styles.note}>
                            {t('providerConfig.sections.advancedReadOnly')}
                        </p>
                    )}
                    <div className={layout.grid}>{advancedFields.map(renderField)}</div>
                </Disclosure>
            )}
            {/* Outside Advanced, so a stored value no field shows is never one click away. */}
            <UnknownExtraConfig group={group} pens={pens} />
        </section>
    );
}

export default ConnectionSection;
export { ConnectionSection };
